import { createApi } from "@reduxjs/toolkit/query/react";
import { createAuthBaseQuery } from "../auth/reauthBaseQuery";
import { appConfig } from "../../appConfig";

import { FilteredPagedModel, FilteredPagedQueryResult, EntityCommonInfoModel, CommonMultiDeleteModel } from "../../entities/common";
import { IndustryModel } from "../../entities/industry";
import { CompanyModel, CompanySelectModel } from "../../entities/company";
import { ActivityGroupModel } from "../../entities/activityGroup";
import { getQuerySearchParams } from "../../utils";

export const TagTypes = {
  Industry: "Industry",
  ActivityGroup: "ActivityGroup",
  Company: "Company",
  Category: "Category",
  ContactMessage: "ContactMessage",
  StatInfo: "StatInfo",
  FinancialStatement: "FinancialStatement",
  Cost: "Cost",
  OaCost: "OaCost",
  MonthlyPerformance: "MonthlyPerformance",
  MonthlyRawMaterial: "MonthlyRawMaterial",
  FcFlow: "FcFlow",
  MonthlyEnergy: "MonthlyEnergy",
  Announcement: "Announcement",
  Product: "Product",
  InventoryTurnover: "InventoryTurnover",
  PcRawMaterial: "PcRawMaterial",
  Oie: "Oie",
  Dps: "Dps",
  InvestmentMatchReview: "InvestmentMatchReview",
  InvestmentReport: "InvestmentReport",
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: createAuthBaseQuery(appConfig.apiUrl),
  tagTypes: Object.values(TagTypes),

  endpoints: (builder) => ({
    // Industry
    getIndustriesCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/industry/commonInfo",
      providesTags: [{ type: TagTypes.Industry, id: "LIST" }],
    }),
    getIndustriesFilteredPaged: builder.query<FilteredPagedQueryResult<IndustryModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false }: FilteredPagedModel) =>
        `/industry/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`,
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TagTypes.Industry, id: "PARTIAL-LIST" }, ...result.data.map(({ id }) => ({ type: TagTypes.Industry, id }))]
          : [{ type: TagTypes.Industry, id: "PARTIAL-LIST" }],
    }),
    getIndustriesSelectFilter: builder.query<IndustryModel[], string>({
      query: (filterText: string) => `/industry/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagTypes.Industry, id: "PARTIAL-LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagTypes.Industry,
          id,
        })),
      ],
    }),
    getIndustry: builder.query<IndustryModel, number>({
      query: (industryId) => `/industry/${industryId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Industry, id: arg }],
    }),
    addIndustry: builder.mutation({
      query: (initialIndustry) => ({
        url: "/industry",
        method: "POST",
        body: initialIndustry,
      }),
      invalidatesTags: [
        { type: TagTypes.Industry, id: "LIST" },
        { type: TagTypes.Industry, id: "PARTIAL-LIST" },
      ],
    }),
    editIndustry: builder.mutation({
      query: (industry) => ({
        url: `/industry/${industry.id}`,
        method: "PUT",
        body: industry,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Industry, id: arg.id },
        { type: TagTypes.Industry, id: "LIST" },
      ],
    }),
    deleteIndustry: builder.mutation({
      query: (industryId) => ({
        url: `/industry/${industryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Industry, id: arg },
              { type: TagTypes.Industry, id: "LIST" },
            ],
    }),
    deleteMultiIndustries: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/industry/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Industry,
                id,
              })),
              { type: TagTypes.Industry, id: "LIST" },
            ],
    }),

    // ActivityGroup
    getActivityGroupsCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/activitygroup/commonInfo",
      providesTags: [{ type: TagTypes.ActivityGroup, id: "LIST" }],
    }),
    getActivityGroupsFilteredPaged: builder.query<FilteredPagedQueryResult<ActivityGroupModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false }: FilteredPagedModel) =>
        `/activitygroup/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`,
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ActivityGroup, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.ActivityGroup,
                id,
              })),
            ]
          : [{ type: TagTypes.ActivityGroup, id: "PARTIAL-LIST" }],
    }),
    getActivityGroupsSelectFilter: builder.query<ActivityGroupModel[], string>({
      query: (filterText: string) => `/activitygroup/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagTypes.ActivityGroup, id: "PARTIAL-LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagTypes.ActivityGroup,
          id,
        })),
      ],
    }),
    getActivityGroup: builder.query<ActivityGroupModel, number>({
      query: (activityGroupId) => `/activitygroup/${activityGroupId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ActivityGroup, id: arg }],
    }),
    addActivityGroup: builder.mutation({
      query: (initialActivityGroup) => ({
        url: "/activitygroup",
        method: "POST",
        body: initialActivityGroup,
      }),
      invalidatesTags: [
        { type: TagTypes.ActivityGroup, id: "LIST" },
        { type: TagTypes.ActivityGroup, id: "PARTIAL-LIST" },
      ],
    }),
    editActivityGroup: builder.mutation({
      query: (activityGroup) => ({
        url: `/activitygroup/${activityGroup.id}`,
        method: "PUT",
        body: activityGroup,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ActivityGroup, id: arg.id },
        { type: TagTypes.ActivityGroup, id: "LIST" },
      ],
    }),
    deleteActivityGroup: builder.mutation({
      query: (activityGroupId) => ({
        url: `/activitygroup/${activityGroupId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ActivityGroup, id: arg },
              { type: TagTypes.ActivityGroup, id: "LIST" },
            ],
    }),
    deleteMultiActivityGroups: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/activitygroup/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ActivityGroup,
                id,
              })),
              { type: TagTypes.ActivityGroup, id: "LIST" },
            ],
    }),

    // Company
    getCompanies: builder.query({
      query: () => "/company",
      providesTags: (result = [], error, arg) => [{ type: TagTypes.Company, id: "LIST" }],
    }),
    getCompanyCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/company/commonInfo",
      providesTags: [{ type: TagTypes.Company, id: "LIST" }],
    }),
    getCompaniesFilteredPaged: builder.query<FilteredPagedQueryResult<CompanyModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/company/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Company, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.Company,
                id,
              })),
            ]
          : [{ type: TagTypes.Company, id: "PARTIAL-LIST" }],
    }),
    getCompaniesSelectFilter: builder.query<CompanySelectModel[], string>({
      query: (filterText: string) => `/company/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagTypes.Company, id: "PARTIAL-LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagTypes.Company,
          id,
        })),
      ],
    }),
    getCompany: builder.query<CompanyModel, number>({
      query: (companyId) => `/company/${companyId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Company, id: arg }],
    }),
    addCompany: builder.mutation({
      query: (initialCompany) => ({
        url: "/company",
        method: "POST",
        body: initialCompany,
      }),
      invalidatesTags: [
        { type: TagTypes.Company, id: "LIST" },
        { type: TagTypes.Company, id: "PARTIAL-LIST" },
      ],
    }),
    editCompany: builder.mutation({
      query: (company) => ({
        url: `/company/${company.id}`,
        method: "PUT",
        body: company,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.Company, id: arg.id }],
    }),
    deleteCompany: builder.mutation({
      query: (companyId) => ({
        url: `/company/${companyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Company, id: arg },
              { type: TagTypes.Company, id: "LIST" },
            ],
    }),
    deleteMultiCompanies: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/company/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Company,
                id,
              })),
              { type: TagTypes.Company, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetIndustriesFilteredPagedQuery,
  useGetIndustriesCommonInfoQuery,
  useGetIndustriesSelectFilterQuery,
  useGetIndustryQuery,
  useAddIndustryMutation,
  useEditIndustryMutation,
  useDeleteIndustryMutation,
  useDeleteMultiIndustriesMutation,

  useGetActivityGroupsCommonInfoQuery,
  useGetActivityGroupsFilteredPagedQuery,
  useGetActivityGroupsSelectFilterQuery,
  useGetActivityGroupQuery,
  useAddActivityGroupMutation,
  useEditActivityGroupMutation,
  useDeleteActivityGroupMutation,
  useDeleteMultiActivityGroupsMutation,

  useGetCompaniesQuery,
  useGetCompanyCommonInfoQuery,
  useGetCompaniesFilteredPagedQuery,
  useGetCompaniesSelectFilterQuery,
  useGetCompanyQuery,
  useAddCompanyMutation,
  useEditCompanyMutation,
  useDeleteCompanyMutation,
  useDeleteMultiCompaniesMutation,
} = apiSlice;
